// Magic-byte image MIME detection. The `<input accept="image/*">` attribute
// and the file's reported MIME type are both spoofable (rename foo.exe to
// foo.png and the browser happily ships type="image/png"). We sniff the
// first 16 bytes against known image signatures so a renamed binary cannot
// reach the editor state.
//
// SVG is text, not binary; checked separately by reading the first 1KB and
// looking for an XML / <svg root tag.

interface Signature {
    mime: string;
    bytes: (number | null)[] | null;
}

const SIGNATURES: Signature[] = [
    { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
    { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
    { mime: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
    {
        mime: 'image/webp',
        bytes: [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50],
    },
    { mime: 'image/svg+xml', bytes: null },
];

function readSliceAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    if (typeof (blob as any).arrayBuffer === 'function') return (blob as any).arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
}

function readSliceAsText(blob: Blob): Promise<string> {
    if (typeof (blob as any).text === 'function') return (blob as any).text();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

export async function sniffImageMime(file: File): Promise<string | null> {
    if (!file || file.size === 0) return null;

    const head = await readSliceAsArrayBuffer(file.slice(0, 16));
    const bytes = new Uint8Array(head);

    for (const sig of SIGNATURES) {
        if (sig.bytes === null) continue;
        if (bytes.length < sig.bytes.length) continue;
        const match = sig.bytes.every((b, i) => b === null || bytes[i] === b);
        if (match) return sig.mime;
    }

    const isLikelySvg = file.type.includes('svg') || file.name.toLowerCase().endsWith('.svg');
    if (isLikelySvg) {
        const text = await readSliceAsText(file.slice(0, 1024));
        const trimmed = text.trimStart();
        if (trimmed.startsWith('<?xml') || trimmed.startsWith('<svg')) return 'image/svg+xml';
    }

    return null;
}
